import type {
  Item,
  Login,
  Slot,
} from "@swisscom/sdx/dist/js/webcomponents/types/components/header/types";
import { devMode } from "../debug";

const SLOT_ORDER = Object.fromEntries(
  Object.entries(["search", "notifications", "cart", "*"]).map(a =>
    a.reverse(),
  ),
);

export const NAME_ACCESSOR = Symbol("name");

function slotOrder(slotName?: string) {
  if (slotName && slotName in SLOT_ORDER) {
    return SLOT_ORDER[slotName];
  }
  return SLOT_ORDER["*"];
}

function compare(a: SlotExternal, b: SlotExternal) {
  return slotOrder(a[NAME_ACCESSOR]) - slotOrder(b[NAME_ACCESSOR]);
}

interface SlotExternal extends Slot {
  [NAME_ACCESSOR]?: string;
}

export type NavigateEvent = CustomEvent<{ item: Login | Item; event?: Event }>;

type NavigateListener = (event: NavigateEvent) => void;

export class HeaderInterface {
  public constructor(private header?: HTMLSdxHeaderElement) {
    if (!header && devMode) {
      console.warn("Initialized HeaderInterface without header element");
    }
  }

  public attachNavigateListener(listener: NavigateListener) {
    const header = this.header;
    if (!header) {
      return;
    }
    header.addEventListener("sdxnavigate", listener as (e: Event) => void);
  }

  public updateSlot(name: string, slot?: SlotExternal) {
    const header = this.header;
    if (!header) {
      return;
    }
    let slots: SlotExternal[] | undefined;
    if (typeof header.slots === "string") {
      slots = JSON.parse(header.slots);
    } else {
      slots = header.slots;
    }
    if (!slots) {
      slots = [];
    }
    slots = slots.map((slot: Slot) => {
      const slotExternal: SlotExternal = { ...slot };
      if (!(NAME_ACCESSOR in slot)) {
        slotExternal[NAME_ACCESSOR] = slot.slot;
      }
      return slotExternal;
    });

    slots = slots.slice(); // Create fresh copy so object is always marked as changed
    const previousSlot = slots.findIndex(sl => sl[NAME_ACCESSOR] === name);
    if (!slot) {
      // Slot needs to be removed
      if (previousSlot > -1) {
        slots.splice(previousSlot, 1);
      }
    } else {
      slot[NAME_ACCESSOR] = name;
      if (previousSlot === -1) {
        if (!("slot" in slot)) {
          slot.slot = name;
        }
        slots.push(slot);
      } else {
        slots.splice(
          previousSlot,
          1,
          Object.assign({}, slots[previousSlot], slot),
        );
      }
    }
    slots.sort(compare);
    header.slots = slots;
  }

  public setLoginData(login?: Login) {
    if (this.header) {
      this.header.login = login;
    }
  }

  public openSlot(name: string) {
    const header = this.header;
    if (!header) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      setTimeout(() => {
        header.openSlot(name).then(resolve, reject);
      }, 10);
    });
  }
}
